import { Injectable } from '@angular/core';
import { from, Observable, of, shareReplay, switchMap } from 'rxjs';

import { InjectableScript, ScriptInjector } from '@common/injectors/script.injector';

const GOOGLE_MAPS_CLUSTER_API_SCRIPT: InjectableScript = {
  id: 'google-maps-cluster',
  src: '/scripts/markerclustererplus.js',
};

/**
 * Lazily loads the Google Maps JS API (via index.html bootstrap + importLibrary)
 * and the marker clusterer script on first demand.
 */
@Injectable({ providedIn: 'root' })
export class GoogleMapsApiHelper {
  private ready$?: Observable<boolean>;
  private clusterReady$?: Observable<boolean>;

  constructor(private scriptInjector: ScriptInjector) {}

  ensureLoaded$(): Observable<boolean> {
    if (!this.ready$) {
      this.ready$ = from(this._loadMapsLibraries()).pipe(
        switchMap(() => of(true)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.ready$;
  }

  ensureClusterLoaded$(): Observable<boolean> {
    if (!this.clusterReady$) {
      this.clusterReady$ = this.ensureLoaded$().pipe(
        switchMap(() => from(this.scriptInjector.injectScript(GOOGLE_MAPS_CLUSTER_API_SCRIPT))),
        switchMap(() => of(true)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.clusterReady$;
  }

  private async _loadMapsLibraries(): Promise<void> {
    const mapsApi = window.google?.maps;
    if (!mapsApi?.importLibrary) {
      throw new Error('Google Maps bootstrap loader is missing from index.html');
    }

    await Promise.all([
      mapsApi.importLibrary('maps'),
      mapsApi.importLibrary('places'),
      mapsApi.importLibrary('geocoding'),
    ]);
  }
}
